import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Collapse,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { FormEvent, useCallback, useState } from "react";
import { Coupon, Product } from "src/api/types";
import { useGetCouponsForProductQuery } from "src/api/services/couponsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAddPatientCouponMutation } from "src/api/services/patientCouponApi";
import LoadingButton from "src/components/Buttons/LoadingButton";

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: number;
}

const CreatePatientCouponModal = ({ onClose, open, patientId }: Props) => {
  const [error, setError] = useState("");
  const { products } = useGetProductsQuery(undefined, {
    selectFromResult: ({ data }) => {
      return { products: data?.data ?? [] };
    },
  });
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const { data: coupons = [] } = useGetCouponsForProductQuery(
    selectedProduct?.slug ?? skipToken,
  );
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [createCoupon, { isLoading }] = useAddPatientCouponMutation();

  const handleProduct = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Product | null) => {
      setSelectedProduct(value);
      setError("");
    },
    [],
  );

  const handleCoupon = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Coupon | null) => {
      setSelectedCoupon(value);
      setError("");
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (selectedCoupon == null || selectedProduct == null) {
        setError("Please select product and coupon");
        return;
      }

      createCoupon({
        patient_id: patientId,
        coupon_id: selectedCoupon?.id,
        product_id: selectedProduct.id,
      })
        .unwrap()
        .then(onClose)
        .catch((err) => setError(err.message));
    },
    [createCoupon, onClose, patientId, selectedCoupon, selectedProduct],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Coupon
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack gap={2}>
          <Autocomplete
            options={products}
            getOptionLabel={(p) => p.name}
            value={selectedProduct}
            onChange={handleProduct}
            renderInput={(params) => (
              <TextField {...params} label="Product" required />
            )}
          />
          <Collapse in={!!selectedProduct} unmountOnExit>
            <Autocomplete
              options={coupons}
              getOptionLabel={(c) => `${c.name} - $${c.amount}`}
              value={selectedCoupon}
              onChange={handleCoupon}
              renderInput={(params) => (
                <TextField {...params} label="Coupon" required />
              )}
            />
          </Collapse>

          <LoadingButton type="submit" loading={isLoading}>
            Create coupon
          </LoadingButton>
        </Stack>
        {error ? <Alert severity={"error"}>{error}</Alert> : null}
      </Stack>
    </Modal>
  );
};

export default CreatePatientCouponModal;
